import {
  AdvanceFilter,
  DeliveryOptions,
  Facets,
  Nutritional,
  ProductSearchFilter,
  ProductSearchFilterType,
  UnitOfMeasureType,
} from '../api';
import { AppRoute, ProductData } from '../models';

export type FindSimilarMode = 'order-confirmation' | 'order-entry' | 'product-list' | 'product-list-notification';

export interface FindSimilarData {
  product: ProductData;
  isSelected: boolean;
  replacedBy: { productKey: string; uom: UnitOfMeasureType; quantity: number }[];
}

export interface FindSimilarIngredientData {
  recipeDetailId: string | undefined;
  recipeIngredientHeaderId: string;
  description: string;
}
export interface ProductSearchHistoryState {
  advanceFilter?: AdvanceFilter;
  cachedFacets?: Facets;
  parentRoute?: AppRoute;
  parentRouteDisplayName?: string;
  findSimilarProducts?: FindSimilarData[];
  activeProductIndex?: number;
  notificationToDismiss?: string;
}

export function isAdvanceFilterUnset(advanceFilter: AdvanceFilter | undefined): boolean {
  return (
    !advanceFilter ||
    (!advanceFilter.Badges.length &&
      !advanceFilter.Brand &&
      !advanceFilter.Brands.length &&
      !advanceFilter.StateOfOriginAbbreviation &&
      !advanceFilter.StateOfOriginAbbreviations.length &&
      !advanceFilter.CategoryIds.length &&
      !Object.keys(advanceFilter.DeliveryOptions).length &&
      advanceFilter.HasOrderedInLastNumberOfDays === undefined &&
      !advanceFilter.HasPreviousPurchase &&
      !advanceFilter.IsLocallyStockedItem &&
      !advanceFilter.IsCriticalItem &&
      !advanceFilter.IsNewInStock &&
      !advanceFilter.IsEarlyCutoffItem &&
      !Object.keys(advanceFilter.Nutritional).length &&
      !advanceFilter.PackSize?.length &&
      !advanceFilter.StorageTypes.length &&
      !advanceFilter.ShoppingCategoryFilters?.length &&
      !advanceFilter.IsExclusiveProduct &&
      !advanceFilter.Manufacturers.length)
  );
}

export function updateAdvanceFilterObject(
  advanceFilter: AdvanceFilter | undefined,
  filters: ProductSearchFilter[]
): AdvanceFilter {
  const result: AdvanceFilter = {
    IsLocallyStockedItem: advanceFilter?.IsLocallyStockedItem,
    IsCriticalItem: advanceFilter?.IsCriticalItem,
    IsNewInStock: advanceFilter?.IsNewInStock,
    IsEarlyCutoffItem: advanceFilter?.IsEarlyCutoffItem,
    HasOrderedInLastNumberOfDays: advanceFilter?.HasOrderedInLastNumberOfDays,
    HasPreviousPurchase: advanceFilter?.HasPreviousPurchase,
    Badges: [...(advanceFilter?.Badges || [])],
    CategoryIds: [...(advanceFilter?.CategoryIds || [])],
    Brand: advanceFilter?.Brand,
    Brands: [...(advanceFilter?.Brands || [])],
    StateOfOriginAbbreviation: advanceFilter?.StateOfOriginAbbreviation,
    StateOfOriginAbbreviations: [...(advanceFilter?.StateOfOriginAbbreviations || [])],
    PackSize: advanceFilter?.PackSize,
    StorageTypes: [...(advanceFilter?.StorageTypes || [])],
    DeliveryOptions: { ...advanceFilter?.DeliveryOptions },
    Nutritional: { ...advanceFilter?.Nutritional },
    ShoppingCategoryFilters: [...(advanceFilter?.ShoppingCategoryFilters || [])],
    IsExclusiveProduct: advanceFilter?.IsExclusiveProduct,
    Manufacturers: [...(advanceFilter?.Manufacturers || [])],
  };
  filters.forEach((filter) => {
    switch (filter.position) {
      case ProductSearchFilterType.Base:
        (result[filter.name as keyof AdvanceFilter] as unknown) = filter.value;
        break;
      case ProductSearchFilterType.NullableBase:
        (result[filter.name as keyof AdvanceFilter] as unknown) = filter.value || undefined;
        break;
      case ProductSearchFilterType.Badges:
      case ProductSearchFilterType.CategoryIds:
      case ProductSearchFilterType.StorageTypes:
      case ProductSearchFilterType.StateOfOriginAbbreviations:
      case ProductSearchFilterType.Manufacturers:
      case ProductSearchFilterType.Brands:
        const tempArray = (result[filter.name as keyof AdvanceFilter] as string[]) || [];
        if (filter.value) {
          tempArray.push(filter.label as string);
        } else {
          const itemIndex = tempArray.indexOf(filter.label as string);
          tempArray.splice(itemIndex, 1);
        }
        (result[filter.name as keyof AdvanceFilter] as string[]) = tempArray;
        break;

      case ProductSearchFilterType.DeliveryOptions:
        result.DeliveryOptions[filter.name as keyof DeliveryOptions] = filter.value || undefined;
        break;
      case ProductSearchFilterType.Program:
      case ProductSearchFilterType.Nutritional:
      case ProductSearchFilterType.Origin:
        result.Nutritional[filter.name as keyof Nutritional] = filter.value || undefined;
        break;
      case ProductSearchFilterType.ShoppingCategoryFilters: {
        const current = result.ShoppingCategoryFilters ?? [];

        if (filter.value) {
          if (!current.includes(filter.label as string)) {
            result.ShoppingCategoryFilters = [...current, filter.label as string];
          }
        } else {
          result.ShoppingCategoryFilters = current.filter((x) => x !== filter.label);
        }
        break;
      }
    }
  });

  return result;
}
